import { FetchRequest, FetchResetStatus } from 'acscp/actions';
import { FormattedMessage } from 'react-intl';
import { Dispatch } from 'redux';
import { AppState } from 'acscp/store';
import { defineMessages, InjectedIntlProps, injectIntl } from 'react-intl';
import React from 'react';
import { RequestStateProps } from 'acscp/components/common/helpers/requestStateProps';
import { withAnonymousUserLayout } from 'acscp/components/layout/anonymousUserLayout';
import { Form } from 'acscp/components/common/form';
import formStyle from 'acscp/components/common/form.module.scss';
import { InputWithValidation } from 'acscp/components/common/validation/inputWithValidation';
import { ButtonWithLoadingIndicator } from 'acscp/components/common/buttonWithLoadingIndicator';
import { FormValidator, ValidationRule } from 'acscp/components/common/helpers/formValidator';
import { StringValidationChecks } from 'acscp/components/common/helpers/validationChecks';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { PasswordResetLinkRequest, RequestClass } from 'acscp/api/requests';
import { Link, RouteComponentProps } from 'react-router-dom';
import { RequestErrorMessage } from 'acscp/components/common/requestErrorMessage';
import { LocationDescriptor } from 'history';

const messages = defineMessages({
    emailError: {
        id: 'web.components.pages.forgottenPasswordPage.emailInvalid',
        defaultMessage: 'Please enter a valid email.'
    },
    emailPlaceholder: {
        id: 'web.components.pages.forgottenPasswordPage.emailPlaceholder',
        defaultMessage: 'EMAIL'
    }
});

interface ForgottenPasswordRouteState {
    returnTo?: LocationDescriptor;
}

type ForgottenPasswordOwnProps = InjectedIntlProps & RouteComponentProps<{}, {}, ForgottenPasswordRouteState>;

interface ForgottenPasswordDispatchProps {
    passwordResetLinkRequest: (email: string) => void;
    resetRequestErrors: () => void;
}
type ForgottenPasswordStateProps = RequestStateProps;

interface ForgottenPasswordForm {
    email: string;
}
type ForgottenPasswordInternalState = ForgottenPasswordForm & {
    showErrors: boolean;
};

type ForgottenPasswordProps = ForgottenPasswordOwnProps & ForgottenPasswordDispatchProps & ForgottenPasswordStateProps;

export class ForgottenPasswordPresentational
    extends React.Component<ForgottenPasswordProps, ForgottenPasswordInternalState> {

    private validator: FormValidator;

    constructor(props: ForgottenPasswordProps) {
        super(props);

        this.validator = new FormValidator(this.getValidationRules());

        this.state = {email: '', showErrors: false};
    }

    private getValidationRules(): ValidationRule[] {
        return [
            {
                validatedPropertyName: 'email',
                validationCheck: StringValidationChecks.isEmail(() => this.state.email),
                errorMessage: this.props.intl.formatMessage(messages.emailError)
            }
        ];
    }

    componentDidMount(): void {
        this.props.resetRequestErrors();
    }

    emailChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ email: event.target.value })

    submit = () => {
        this.props.resetRequestErrors();

        if (!this.props.isLoading) {
            if (this.validator.isStateValid()) {
                this.props.passwordResetLinkRequest(this.state.email);
            }
        }

        this.setState({showErrors: true});
    }

    getInitialFormContent(): JSX.Element[] {
        const {intl, isLoading, requestError} = this.props;

        const validationErrors = this.validator.getDisplayedErrors(this.state.showErrors, requestError);

        return ([
                (
                    <FormattedMessage
                        key={0}
                        id={'web.components.pages.forgottenPassword.description'}
                        defaultMessage={'Enter your email address and we\'ll send you a reset password link.'}
                    />
                ),
                (
                    <InputWithValidation
                        key={1}
                        type="email"
                        name="email"
                        value={this.state.email}
                        autoComplete="username"
                        placeholder={intl.formatMessage(messages.emailPlaceholder)}
                        onChange={this.emailChanged}
                        validationErrors={validationErrors}
                        autoFocus={true}
                    />
                ),
                (
                    <ButtonWithLoadingIndicator key={2} isLoading={isLoading}>
                        <FormattedMessage
                            id={'web.components.pages.forgottenPassword.submit'}
                            defaultMessage={'REQUEST RESET'}
                        />
                    </ButtonWithLoadingIndicator>
                ),
                (<RequestErrorMessage key={3} requestState={this.props} />)
        ]);
    }

    private getReturnTo(): string {
        const params = new URLSearchParams(this.props.location.search || undefined);
        return params.get('returnTo') || '/login';
    }

    render(): JSX.Element {
        return (
            <Form
                header={
                    <FormattedMessage
                        id={'web.components.pages.forgottenPassword.header'}
                        defaultMessage={'PASSWORD RESET'}
                    />
                }
                submit={this.submit}
            >
                {this.props.success
                    ?
                        <FormattedMessage
                            id={'web.components.pages.forgottenPassword.success'}
                            defaultMessage={'If this email address is recognised you will receive a reset link.'}
                        />
                    : this.getInitialFormContent()}
                <Link to={this.getReturnTo()} className={formStyle.TextLink}>
                    <FormattedMessage
                        id={'web.components.pages.forgottenPassword.cancel'}
                        defaultMessage={'Cancel'}
                    />
                </Link>
            </Form>
        );
    }
}

function mapStateToProps(
    state: AppState,
    ownProps: ForgottenPasswordOwnProps,
    getRequestError: (request: RequestClass<any>) => RequestStateProps
): ForgottenPasswordStateProps {
    return getRequestError(PasswordResetLinkRequest);
}

function mapDispatchToProps(
    dispatch: Dispatch, ownProps: ForgottenPasswordOwnProps
): ForgottenPasswordDispatchProps {
    return {
        passwordResetLinkRequest: (email: string) =>
            dispatch(new FetchRequest(new PasswordResetLinkRequest(email))),
        resetRequestErrors: () => dispatch(new FetchResetStatus(PasswordResetLinkRequest))
    };
}

const ForgottenPasswordPage = withAnonymousUserLayout(
    connectWithRequests(mapStateToProps, mapDispatchToProps)(
        injectIntl(
            ForgottenPasswordPresentational
        )
    )
);

export { ForgottenPasswordPage };
